/**
 * Bandeaux d'état : erreur d'API, succès, avertissement.
 *
 * Le bandeau d'erreur est le seul endroit du back-office qui affiche le **message du serveur** :
 * c'est ce qu'un administrateur doit lire pour corriger un brouillon refusé. Il affiche aussi les
 * erreurs de validation détaillées (`chemin` + `message`) quand le serveur en fournit, et propose
 * une reprise quand l'action est rejouable sans risque.
 */
import { Bouton } from './Bouton';
import { erreursDeValidation, type ErreurApi } from '../api/erreurs';
import { M, messageErreurCode } from '../catalogue/messages';

export interface ProprietesBandeauErreur {
  readonly erreur: ErreurApi;
  /** Fourni quand l'action peut être rejouée sans risque. */
  readonly surReessai?: () => void;
  /** Titre facultatif, pour situer le refus (ex. « Publication refusée »). */
  readonly titre?: string;
}

export function BandeauErreur({ erreur, surReessai, titre }: ProprietesBandeauErreur) {
  const defauts = erreursDeValidation(erreur);
  const messageServeur = erreur.reseau
    ? M.erreurs.reseau
    : (erreur.messageServeur ?? messageErreurCode(erreur.code));
  return (
    <div className="bandeau bandeau--erreur" role="alert">
      <p className="bandeau__titre">{titre ?? M.erreurs.generique}</p>
      <p className="bandeau__message">{messageServeur}</p>
      {defauts.length === 0 ? null : (
        <div className="bandeau__details">
          <p className="bandeau__details-titre">{M.erreurs.detailsTitre}</p>
          <ul>
            {defauts.map((defaut, index) => (
              <li key={`${defaut.chemin}-${String(index)}`}>
                <code>{defaut.chemin}</code> — {defaut.message}
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="bandeau__code">{M.erreurs.code(erreur.reseau ? 'RESEAU' : erreur.code)}</p>
      {surReessai === undefined ? null : (
        <Bouton variante="secondaire" onClick={surReessai}>
          {M.commun.reessayer}
        </Bouton>
      )}
    </div>
  );
}

export interface ProprietesBandeauSimple {
  readonly texte: string;
  readonly ton?: 'succes' | 'info' | 'avertissement';
}

export function Bandeau({ texte, ton = 'info' }: ProprietesBandeauSimple) {
  return (
    <div className={`bandeau bandeau--${ton}`} role={ton === 'avertissement' ? 'alert' : 'status'}>
      <p className="bandeau__message">{texte}</p>
    </div>
  );
}
